import { useMemo } from 'react';
import '@/styles/animations.css';

/**
 * Props for the SparkleBackground component
 */
interface SparkleBackgroundProps {
  /** Optional CSS class name for container */
  className?: string;
  /** Number of sparkle particles to render (default: 20) */
  particleCount?: number;
  /** Density of sparkles: 'light', 'medium', or 'dense' (default: 'medium') */
  density?: 'light' | 'medium' | 'dense';
  /** Enable gold sparkles (default: true) */
  showGold?: boolean;
  /** Enable rose sparkles (default: true) */
  showRose?: boolean;
  /** Enable champagne sparkles (default: true) */
  showChampagne?: boolean;
}

/**
 * Configuration for particle density presets
 */
const DENSITY_PRESETS = {
  light: 10,
  medium: 20,
  dense: 35,
} as const;

/**
 * Sparkle color variants with their CSS classes and gradient styles
 */
const SPARKLE_VARIANTS = {
  gold: {
    className: 'sparkle-gold',
    gradient: 'radial-gradient(circle, #ffd700 0%, rgba(255, 215, 0, 0.3) 50%, transparent 70%)',
  },
  rose: {
    className: 'sparkle-rose',
    gradient: 'radial-gradient(circle, #ff8fa3 0%, rgba(255, 143, 163, 0.3) 50%, transparent 70%)',
  },
  champagne: {
    className: 'sparkle-champagne',
    gradient: 'radial-gradient(circle, #f7e7ce 0%, rgba(247, 231, 206, 0.3) 50%, transparent 70%)',
  },
} as const;

type SparkleVariant = keyof typeof SPARKLE_VARIANTS;

/**
 * Individual sparkle particle data
 */
interface SparkleParticle {
  id: number;
  variant: SparkleVariant;
  size: number;
  top: string;
  left: string;
  animationDelay: string;
  animationDuration: string;
  animationType: 'sparkle' | 'twinkle';
}

/**
 * Generate a random number between min and max
 */
function randomBetween(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

/**
 * Generate sparkle particles with randomized positions and animations
 */
function generateSparkles(
  count: number,
  enabledVariants: SparkleVariant[]
): SparkleParticle[] {
  return Array.from({ length: count }, (_, i) => {
    const variant = enabledVariants[i % enabledVariants.length];
    const animationType = Math.random() > 0.5 ? 'sparkle' : 'twinkle';

    return {
      id: i,
      variant,
      size: randomBetween(3, 8),
      top: `${randomBetween(5, 95)}%`,
      left: `${randomBetween(5, 95)}%`,
      animationDelay: `${randomBetween(0, 5)}s`,
      animationDuration: animationType === 'sparkle'
        ? `${randomBetween(1.5, 3)}s`
        : `${randomBetween(2, 4)}s`,
      animationType,
    };
  });
}

/**
 * Individual sparkle particle component
 */
function SparkleParticleElement({ particle }: { particle: SparkleParticle }) {
  const variant = SPARKLE_VARIANTS[particle.variant];

  return (
    <div
      className={`sparkle-particle ${variant.className}`}
      style={{
        width: `${particle.size}px`,
        height: `${particle.size}px`,
        top: particle.top,
        left: particle.left,
        background: variant.gradient,
        animation: `${particle.animationType} ${particle.animationDuration} ease-in-out infinite`,
        animationDelay: particle.animationDelay,
      }}
    />
  );
}

/**
 * Large gradient orb for ambient glow effects
 */
function GlowOrb({
  position,
  size,
  color,
  delay = 0,
}: {
  position: { top?: string; bottom?: string; left?: string; right?: string };
  size: string;
  color: string;
  delay?: number;
}) {
  return (
    <div
      className="absolute rounded-full animate-float"
      style={{
        ...position,
        width: size,
        height: size,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        filter: 'blur(50px)',
        opacity: 0.15,
        animationDuration: `${8 + delay}s`,
        animationDelay: `${delay}s`,
      }}
    />
  );
}

/**
 * SparkleBackground Component
 *
 * An animated sparkle/particle overlay for creating a vibrant, celebratory atmosphere.
 * Renders multiple sparkle particles with randomized positions and animations in gold,
 * rose, and champagne color variants.
 *
 * Features:
 * - Randomized particle positions and sizes
 * - Multiple sparkle color variants (gold, rose, champagne)
 * - Two animation types: sparkle (scale + rotate) and twinkle (scale + opacity)
 * - Ambient glow orbs for depth
 * - Respects prefers-reduced-motion media query
 * - Fully accessible (decorative, hidden from screen readers)
 *
 * @example
 * ```tsx
 * // Basic usage
 * <SparkleBackground />
 *
 * // With custom density
 * <SparkleBackground density="dense" />
 *
 * // With only gold sparkles
 * <SparkleBackground showRose={false} showChampagne={false} />
 *
 * // With custom class for positioning
 * <SparkleBackground className="z-0" />
 * ```
 */
export function SparkleBackground({
  className = '',
  particleCount,
  density = 'medium',
  showGold = true,
  showRose = true,
  showChampagne = true,
}: SparkleBackgroundProps) {
  // Determine particle count based on density or explicit count
  const count = particleCount ?? DENSITY_PRESETS[density];

  // Build list of enabled sparkle variants
  const enabledVariants = useMemo((): SparkleVariant[] => {
    const variants: SparkleVariant[] = [];
    if (showGold) variants.push('gold');
    if (showRose) variants.push('rose');
    if (showChampagne) variants.push('champagne');
    // Default to gold if all disabled
    if (variants.length === 0) {
      variants.push('gold');
    }
    return variants;
  }, [showGold, showRose, showChampagne]);

  // Generate sparkle particles (memoized for performance)
  const sparkles = useMemo(
    () => generateSparkles(count, enabledVariants),
    [count, enabledVariants]
  );

  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      aria-hidden="true"
    >
      {/* Sparkle particles */}
      {sparkles.map((particle) => (
        <SparkleParticleElement key={particle.id} particle={particle} />
      ))}

      {/* Ambient glow orbs for depth and warmth */}
      <GlowOrb
        position={{ top: '-10%', left: '-5%' }}
        size="300px"
        color="rgba(255, 215, 0, 0.25)"
        delay={0}
      />
      <GlowOrb
        position={{ bottom: '-15%', right: '-10%' }}
        size="400px"
        color="rgba(255, 143, 163, 0.2)"
        delay={2}
      />
      <GlowOrb
        position={{ top: '40%', right: '10%' }}
        size="200px"
        color="rgba(247, 231, 206, 0.2)"
        delay={4}
      />
    </div>
  );
}

export default SparkleBackground;
